import { useEffect, useRef } from 'react';

/**
 * A notice that goes away on its own, and at the first sign the user has moved on.
 *
 * Returns the ref to put on the element that counts as "inside": a pointer press anywhere else
 * dismisses, as does any key, as does `timeoutMs` of being ignored. Listeners are registered in the
 * capture phase, so a handler that stops propagation on the way up cannot keep the notice alive.
 */
export function useTransientDismiss<T extends HTMLElement>(
  active: boolean,
  onDismiss: () => void,
  timeoutMs = 5000,
) {
  const ref = useRef<T | null>(null);
  const dismiss = useRef(onDismiss);

  useEffect(() => {
    dismiss.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!active) return;

    const timer = window.setTimeout(() => dismiss.current(), timeoutMs);

    function onKeyDown() {
      dismiss.current();
    }

    function onPointerDown(event: PointerEvent) {
      if (event.target instanceof Node && ref.current?.contains(event.target)) return;
      dismiss.current();
    }

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [active, timeoutMs]);

  return ref;
}
